/**
 * 進貨單列印頁(REQ-20260814001)。進貨管理原本完全沒有列印入口 ——
 * 這是為了與出貨管理列表對齊而補的第三個動作。
 *
 * 與藝品印刷訂單(`OrderPrintPage`)同一套做法:**不引進 PDF 套件**,走瀏覽器列印
 * (Cmd+P → 另存 PDF),列印樣式沿用 `styles/globals.css` 的 `@media print` /
 * `.print-area`。不同的是紙張方向 —— 那張單為了稿樣區要橫向,所以自己覆寫 `@page`;
 * 進貨單是純表格,用全域的直向 A4 就好,這裡不覆寫。
 *
 * 每一格都來自既有的 `PurchaseOrder` + `PurchaseOrderLine`,不是新的資料表
 * (同 REQ-20260806003 的結論:另開一張表就會變成第二份真相)。
 * **唯一的例外是廠商的統編 / 電話**:單據上只存了廠商 id 與名稱快照,沒有存這兩格,
 * 所以額外向廠商主檔要一次(`GET /suppliers/:id`,權限與進貨單明細相同)。
 *
 * REQ-20260818002 起有**兩種版面**,依進貨類別二選一:
 *  - 派工類(`isWorkOrderCategory()`)→ `WorkOrderSheet`,貼近紙本派工單的規格表
 *  - 其餘 → 既有的 `PurchaseOrderSheet`(訂購單 / 紙品採購單的品項表格版,一字未改)
 * 兩者共用同一份資料與同一套 `Cell` / `Amount` 元件,不是兩套邏輯。
 */
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PURCHASE_ORDER_STATUSES,
  PURCHASE_TAX_CATEGORIES,
  PURCHASE_TAX_MODES,
  effectiveTaxCategory,
  purchaseTaxModeFromFields,
  type CompanyProfile,
  type PurchaseOrderDetail,
  type PurchaseTaxCategory,
  type SupplierView,
} from '@app/shared';
import { ExportJpegButton } from '../../components/ExportJpegButton';
import { getApiErrorMessage } from '../../lib/api-client';
import { buildDocumentFilename } from '../../lib/filename';
import { formatMoney } from '../../lib/format-money';
import { referenceNameMap, useReferences } from '../settings/api';
import { useSupplier } from '../suppliers/api';
import { useCompanyProfile, usePurchaseOrder } from '../accounting/api';
import { isWorkOrderCategory } from './work-order-category';

export function PurchaseOrderPrintPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const orderId = id ? Number(id) : undefined;
  const { data: order, isLoading, isError, error } = usePurchaseOrder(orderId);
  // 統編 / 電話只有主檔有;撈不到(權限、廠商已刪)就讓那兩格留白,不擋住整張單
  const { data: supplier } = useSupplier(order?.supplierId);
  /**
   * 進貨類別的 code → 中文(REQ-20260814002)。`all=true`:這張單的類別可能事後被停用,
   * 印出來仍要是當初的名稱。**載入中要一起擋住整頁**(見下面的 isLoading)——
   * 印在紙上的東西沒有第二次機會,先印出 code 再變成中文是看不出來的錯。
   * 清單真的撈不到時(權限 / API 掛掉)才退回顯示原始 code。
   */
  const { data: purchaseCategories, isLoading: categoriesLoading } = useReferences(
    'purchase-categories',
    true,
  );
  const categoryNames = referenceNameMap(purchaseCategories?.data);
  const navigate = useNavigate();

  /*
   * 「關閉頁面」的兩條路徑判斷與 `OrderPrintPage` 相同:清單的列印連結是
   * `target="_blank"`,session history 只有一筆,`window.close()` 關得掉;
   * 直接改網址列進來的關不掉,那就導回進貨管理分頁。
   * 判斷用 history 長度而不是 `window.opener` —— 連結帶了 `rel="noreferrer"`,
   * opener 一律是 null,拿它判斷會把兩條路徑都當成同分頁導覽。
   */
  const closePage = (): void => {
    if (window.history.length <= 1) {
      window.close();
      return;
    }
    navigate('/accounting?tab=purchase');
  };

  /*
   * 這張單的抬頭字:派工類印進貨類別的名稱(印刷派工單 / 裁型代工單…),其餘印「進貨單」。
   * 判斷沿用 `isWorkOrderCategory()`,不在這裡重寫一份 —— 下面選版面用的是同一個值,
   * 檔名與紙上的抬頭因此不會講兩種話。
   */
  const sheetTitle = order
    ? isWorkOrderCategory(categoryNames[order.orderType])
      ? (categoryNames[order.orderType] ?? '派工單')
      : '進貨單'
    : '';

  /*
   * Cmd+P → 另存 PDF 時,存檔對話框的預設檔名就是分頁標題(REQ-20260811002 為出貨單
   * 做過同一件事)。離開本頁要還原,否則分頁會一直卡著單號。
   *
   * REQ-20260903006 起檔名改成「表單類別-廠商簡稱-單號」,與出貨單那頁同一個格式。
   * effect 的三個做法照舊(掛在早退之前 / cleanup 還原前值 / 依賴掛那個字串)。
   */
  const documentFilename = order
    ? buildDocumentFilename([sheetTitle, order.supplierSimpleName, order.orderNo])
    : '';
  useEffect(() => {
    if (!documentFilename) return;
    const previousTitle = document.title;
    document.title = documentFilename;
    return () => {
      document.title = previousTitle;
    };
  }, [documentFilename]);

  /** 「另存 JPEG」要截的那一塊:底下兩種版面共用的 `.print-area`(REQ-20260903006) */
  const sheetRef = useRef<HTMLDivElement>(null);

  if (isLoading || categoriesLoading) {
    return (
      <div role="status" className="text-slate-500">
        載入中...
      </div>
    );
  }
  if (isError || !order) {
    return (
      <div role="alert" className="text-red-600">
        載入失敗：{getApiErrorMessage(error, '未知錯誤')}
      </div>
    );
  }

  return (
    <div className="grid gap-4" ref={sheetRef}>
      <div className="flex items-center gap-3 no-print">
        {/* 同一份單據的第二種存檔格式(REQ-20260903006);PDF 那條路一行未動 */}
        <ExportJpegButton targetRef={sheetRef} filename={documentFilename} />
        <button
          type="button"
          onClick={() => window.print()}
          className="border border-blue-600 text-blue-700 px-3 py-1.5 rounded hover:bg-blue-50 text-sm"
          data-testid="po-print-btn"
        >
          列印 / 存 PDF
        </button>
        <button
          type="button"
          onClick={closePage}
          className="bg-slate-200 text-slate-700 px-3 py-1.5 rounded hover:bg-slate-300 text-sm"
          data-testid="po-print-close-btn"
        >
          關閉頁面
        </button>
        <span className="text-slate-500 text-xs">列印時請選 A4、紙張方向直向</span>
      </div>

      {/*
        版面依進貨類別二選一(REQ-20260818002):派工類印生產指示版(貼近紙本的
        印刷派工單 / 裁型代工單),其餘維持原本的訂購單 / 紙品採購單表格版。
        兩者是同一張單據的兩種呈現,不是兩套資料。
      */}
      {isWorkOrderCategory(categoryNames[order.orderType]) ? (
        <WorkOrderSheet order={order} supplier={supplier} title={sheetTitle} />
      ) : (
        <PurchaseOrderSheet order={order} supplier={supplier} categoryNames={categoryNames} />
      )}
    </div>
  );
}

/**
 * 派工單版面(REQ-20260818002)—— 發包給代工廠的生產指示單。
 *
 * 欄位配置與標籤文字盡量貼近紙本(代工單位 / 精樣名稱 / 印刷用紙 / 紙張尺寸 /
 * 印工張數 / 印刷顏色 / 特殊項目 / 是否校色 / 印刷數量 / 後製加工 / 成品尺寸 /
 * 成品數量 / 交件日期 / 取件方式 / 用紙廠商 / 備註 / 稿樣)。
 * 標題直接用**進貨類別的名稱**:紙本有印刷派工單 / 信封成型派工單 / 裁型代工單
 * 好幾種變體,使用者在設定頁怎麼命名就怎麼印,不在程式裡再做一份對照表。
 *
 * 紙本是「一張單一個精樣」,系統的單可以有多個品項 —— 所以每個品項印一個規格區塊。
 * 金額區照印:這張單同時是應付的來源憑證,代工費要看得到。
 */
function WorkOrderSheet({
  order,
  supplier,
  title,
}: {
  order: PurchaseOrderDetail;
  supplier: SupplierView | undefined;
  title: string;
}): JSX.Element {
  const { data: company } = useCompanyProfile();
  return (
    <div
      className="print-area mx-auto bg-white text-black shadow-sm rounded-md p-6 text-sm w-full max-w-4xl"
      data-testid="po-print-sheet"
    >
      <header className="border-b border-black pb-2">
        <div className="flex items-baseline justify-between">
          <h1 className="text-xl font-bold tracking-widest">{title}</h1>
          <span className="text-xs text-slate-600">
            {PURCHASE_ORDER_STATUSES[order.status as keyof typeof PURCHASE_ORDER_STATUSES] ??
              order.status}
          </span>
        </div>
        <CompanyLine company={company} />
      </header>

      <div className="mt-3 grid gap-3 grid-cols-2">
        <section className="border border-black">
          <Cell label="單號" value={order.orderNo} />
          {/* 紙本的「代工單位」就是系統的廠商 —— 這張單的應付掛在它名下 */}
          <Cell label="代工單位" value={order.supplierFullName} />
          <Cell label="統一編號" value={supplier?.tax ?? null} />
          <Cell label="電話" value={primaryPhone(supplier)} />
        </section>

        <section className="border border-black">
          <Cell label="派工日期" value={order.txnDate} />
          <Cell label="交件日期" value={order.dueDate} />
          <Cell label="取件方式" value={order.pickupMethod} />
          {/* 用紙廠商是另一家 —— 只是註明紙的來源,不產生第二筆應付 */}
          <Cell label="用紙廠商" value={order.paperSupplierFullName} />
        </section>
      </div>

      {order.lines.map((line) => (
        <section
          key={line.id}
          className="mt-3 border border-black"
          data-testid={`po-print-work-order-line-${line.lineNo}`}
        >
          <div className="border-b border-black px-2 py-1 text-xs font-medium bg-slate-50">
            第 {line.lineNo} 項
          </div>
          <div className="grid grid-cols-2">
            <Cell label="精樣名稱" value={line.productName} />
            <Cell label="印刷用紙" value={line.productSpec} />
            <Cell label="紙張尺寸" value={line.sizeSpec} />
            <Cell label="印工張數" value={line.impressionQty != null ? String(line.impressionQty) : null} />
            <Cell label="印刷顏色" value={line.printColor} />
            <Cell label="是否校色" value={line.colorProofRequired ? '是' : '否'} />
            <Cell label="印刷數量" value={`${line.qty}${line.unitName ?? ''}`} />
            <Cell label="特殊項目" value={line.specialItems} />
            <Cell label="成品尺寸" value={line.finishedSize} />
            <Cell label="成品數量" value={line.finishedQty != null ? String(line.finishedQty) : null} />
            <Cell label="後製加工" value={line.postProcessing} />
            {/* 一項可對應多張出貨單(REQ-20260905003)—— 用「、」串起來,
                與表單那格的分隔符一致。一張都沒挑就給 null,維持既有的留白行為 */}
            <Cell
              label="工單 / 案號"
              value={line.workOrderNos.length > 0 ? line.workOrderNos.join('、') : null}
            />
          </div>
        </section>
      ))}

      {/*
        稿樣。**這一版只做到「附件上傳」**(需求明確排除圖片上的尺寸標註編輯器),
        所以這裡印的是附件檔名清單 + 一塊空白供現場手繪標註 —— 與紙本的作法相同。
        印出圖片本身要走 signed-url 逐張取,列印前還得等圖片載完才不會印出空框,
        那是另一個要在瀏覽器實測的題目,這次不做(理由寫進 REQ 文件)。
      */}
      <section className="mt-3 border border-black" data-testid="po-print-artwork">
        <div className="border-b border-black px-2 py-1 text-xs font-medium">稿樣</div>
        <div className="p-2 min-h-[8rem]">
          {order.attachments.length > 0 ? (
            <ul className="text-xs list-disc pl-4">
              {order.attachments.map((a) => (
                <li key={a.id}>{a.originalName}</li>
              ))}
            </ul>
          ) : null}
        </div>
      </section>

      <div className="mt-3 grid gap-3 grid-cols-3">
        <section className="border border-black col-span-2">
          <div className="border-b border-black px-2 py-1 text-xs font-medium">備註</div>
          <div className="p-2 min-h-[4rem] whitespace-pre-wrap" data-testid="po-print-notes">
            {order.notes ?? ''}
          </div>
          {/*
            紙本右下角的兩個核銷框。**不另開欄位**:語意已由既有的入庫類型與成本去向
            涵蓋(需求明確要求沿用),所以這裡是「呈現現有狀態」而不是可勾選的欄位。
              已入庫 = 已確認過帳、且不是不入庫直送
              轉加工 = 有品項的成本去向是「直接進工單」(東西不進倉,直接投產)
          */}
          <div
            className="border-t border-black px-2 py-1 flex gap-6 text-xs"
            data-testid="po-print-fulfillment"
          >
            <CheckBox label="已入庫" checked={order.status === 'POSTED' && order.stockInType !== 'DIRECT_SHIP'} />
            <CheckBox
              label="轉加工"
              checked={order.lines.some((l) => l.costDestination === 'DIRECT_TO_WORK_ORDER')}
            />
          </div>
        </section>

        <section className="border border-black" data-testid="po-print-amounts">
          <div className="border-b border-black px-2 py-1 text-xs font-medium">金額統計區</div>
          <dl className="p-2 grid gap-1">
            <Amount label="未稅金額" value={order.subtotal} />
            <Amount label="稅額" value={order.tax} />
            <div className="flex items-baseline justify-between border-t border-black pt-1">
              <dt className="text-sm">含稅總計</dt>
              <dd className="text-xl font-bold text-red-600 font-mono">
                {formatMoney(order.total)}
              </dd>
            </div>
            <Amount label="未結" value={order.outstanding} />
          </dl>
        </section>
      </div>
    </div>
  );
}

/** 紙本上的核銷框。狀態由單據既有欄位推出來,不是可勾選的輸入 */
function CheckBox({ label, checked }: { label: string; checked: boolean }): JSX.Element {
  return (
    <span>
      {checked ? '☑' : '☐'} {label}
    </span>
  );
}

/**
 * 品項印出來的稅別文字(REQ-20260902011)。
 *
 * 品項留空 = 沿用單頭(既有語意,不動);拿到生效稅別之後再翻成表單上那組值域
 * (含稅 / 稅外加 / 免稅)—— 「含稅 / 稅外加」的差別在單頭的 `unitPriceTaxInclusive`,
 * 所以要連單頭一起看,不能只看 `taxCategory`。
 * 對不回去的舊值(零稅率,廠商主檔沒有這個稅別)照舊印「零稅率」。
 */
function lineTaxLabel(
  lineCategory: string | null | undefined,
  order: PurchaseOrderDetail,
): string {
  const category = effectiveTaxCategory(
    lineCategory as PurchaseTaxCategory | null,
    order.taxCategory as PurchaseTaxCategory,
  );
  const mode = purchaseTaxModeFromFields(category, order.unitPriceTaxInclusive);
  return mode ? PURCHASE_TAX_MODES[mode] : PURCHASE_TAX_CATEGORIES[category];
}

function PurchaseOrderSheet({
  order,
  supplier,
  categoryNames,
}: {
  order: PurchaseOrderDetail;
  supplier: SupplierView | undefined;
  categoryNames: Record<string, string>;
}): JSX.Element {
  // 我方公司抬頭(REQ-20260817004)。沒設定過時各欄位印「—」,不擋住列印
  const { data: company } = useCompanyProfile();
  return (
    <div
      className="print-area mx-auto bg-white text-black shadow-sm rounded-md p-6 text-sm w-full max-w-4xl"
      data-testid="po-print-sheet"
    >
      <header className="border-b border-black pb-2">
        <div className="flex items-baseline justify-between">
          <h1 className="text-xl font-bold tracking-widest">進貨單</h1>
          <span className="text-xs text-slate-600">
            {PURCHASE_ORDER_STATUSES[order.status as keyof typeof PURCHASE_ORDER_STATUSES] ??
              order.status}
          </span>
        </div>
        {/* 進貨單是交給廠商的單據 —— 上面看不出是誰下的單並不合理 */}
        <CompanyLine company={company} />
      </header>

      <div className="mt-3 grid gap-3 grid-cols-2">
        <section className="border border-black">
          <Cell label="單號" value={order.orderNo} />
          {/* 「採購日期」= 跟廠商下訂那天(`txnDate`,單號的年月日就是它);
              「到貨日期」= 實際到貨入庫那天,沒補填就留白(REQ-20260916001)。
              派工單版面的「派工日期」/「交件日期」是另一組概念,一字未動 */}
          <Cell label="採購日期" value={order.txnDate} />
          <Cell label="到貨日期" value={order.receivedDate} />
          <Cell label="進貨類別" value={categoryNames[order.orderType] ?? order.orderType} />
          {/* 代管客戶只有發外包加工那類的單才有,沒有就整格不印 */}
          {order.customerSimpleName ? (
            <Cell label="代管客戶" value={order.customerSimpleName} />
          ) : null}
        </section>

        <section className="border border-black">
          <Cell label="廠商" value={order.supplierFullName} />
          <Cell label="統一編號" value={supplier?.tax ?? null} />
          {/* 地址取單據上的收貨地址 —— 那是這張單當初談定的,不是廠商主檔的現值 */}
          <Cell label="地址" value={order.receivingAddress ?? supplier?.address ?? null} />
          <Cell label="電話" value={primaryPhone(supplier)} />
        </section>
      </div>

      {/* 發票號碼 / 日期沒有時整塊不印:目前建單表單沒有這兩格的入口(REQ-20260813002),
          多數單會是空的,印一排空欄位只會讓人以為漏填 */}
      {order.invoiceNo || order.invoiceDate ? (
        <div className="mt-3 grid gap-3 grid-cols-2">
          <section className="border border-black">
            <Cell label="發票號碼" value={order.invoiceNo} />
          </section>
          <section className="border border-black">
            <Cell label="發票日期" value={order.invoiceDate} />
          </section>
        </div>
      ) : null}

      <table className="mt-3 w-full border border-black border-collapse" data-testid="po-print-lines">
        <thead>
          <tr className="border-b border-black text-left">
            <Th className="w-8">#</Th>
            <Th>品名</Th>
            <Th>規格</Th>
            <Th className="text-right">數量</Th>
            <Th className="text-right">單價</Th>
            <Th>稅別</Th>
          </tr>
        </thead>
        <tbody>
          {order.lines.map((line) => (
            <tr key={line.id} className="border-b border-black last:border-b-0">
              <Td>{line.lineNo}</Td>
              <Td>{line.productName}</Td>
              <Td>{line.productSpec ?? ''}</Td>
              <Td className="text-right font-mono">
                {line.qty}
                {line.unitName ?? ''}
              </Td>
              <Td className="text-right font-mono">{line.unitPrice}</Td>
              {/* 品項稅別留空代表沿用單頭,不是「沒有稅別」—— 印出來要是實際生效的那個。
                  文字自 REQ-20260902011 起與表單 / 廠商主檔一致(含稅 / 稅外加 / 免稅);
                  對不回稅別的舊值(零稅率)照舊印零稅率,不要在紙上改寫舊單 */}
              <Td>{lineTaxLabel(line.taxCategory, order)}</Td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 grid gap-3 grid-cols-3">
        <section className="border border-black col-span-2">
          <div className="border-b border-black px-2 py-1 text-xs font-medium">備註</div>
          <div className="p-2 min-h-[5rem] whitespace-pre-wrap" data-testid="po-print-notes">
            {order.notes ?? ''}
          </div>
        </section>

        <section className="border border-black" data-testid="po-print-amounts">
          <div className="border-b border-black px-2 py-1 text-xs font-medium">金額統計區</div>
          <dl className="p-2 grid gap-1">
            <Amount label="未稅金額" value={order.subtotal} />
            <Amount label="稅額" value={order.tax} />
            <div className="flex items-baseline justify-between border-t border-black pt-1">
              <dt className="text-sm">含稅總計</dt>
              <dd className="text-xl font-bold text-red-600 font-mono">
                {formatMoney(order.total)}
              </dd>
            </div>
            <Amount label="未結" value={order.outstanding} />
          </dl>
        </section>
      </div>
    </div>
  );
}

/**
 * 我方公司抬頭(REQ-20260817004)。寫法與藝品印刷訂單那張一致:一行 `text-xs`,
 * 沒填的欄位印「—」而不是留白 —— 下面 `Cell` 的留白是「這格沒有資料」,
 * 公司抬頭則是「還沒去設定頁填」,兩者要分得出來。
 */
function CompanyLine({ company }: { company: CompanyProfile | undefined }): JSX.Element {
  return (
    <div className="mt-1 flex flex-wrap gap-x-4 text-xs" data-testid="po-print-company">
      <span className="font-medium">{company?.name || '—'}</span>
      <span>地址:{company?.address || '—'}</span>
      <span>電話:{company?.phone || '—'}</span>
      <span>傳真:{company?.fax || '—'}</span>
    </div>
  );
}

/** 主要電話:優先取手機,沒有才取電話清單第一筆(與 `PartyInfo.phone` 同一條規則) */
function primaryPhone(supplier: SupplierView | undefined): string | null {
  if (!supplier) return null;
  const mobile = supplier.phones.find((p) => p.phoneType === 'MOBILE');
  return (mobile ?? supplier.phones[0])?.number ?? null;
}

/** 一格:標籤 + 值。值為空時留白,不要印「—」佔住格子(同藝品印刷訂單) */
function Cell({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <div className="flex border-b border-black last:border-b-0 min-h-[2rem]">
      <span className="w-24 shrink-0 border-r border-black px-2 py-1 text-xs bg-slate-50">
        {label}
      </span>
      <span className="px-2 py-1 break-all">{value ?? ''}</span>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <th className={`border-r border-black last:border-r-0 px-2 py-1 text-xs ${className ?? ''}`}>{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <td className={`border-r border-black last:border-r-0 px-2 py-1 ${className ?? ''}`}>{children}</td>;
}

function Amount({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-sm">{label}</dt>
      <dd className="font-mono">{formatMoney(value)}</dd>
    </div>
  );
}
